export interface ContextValue {
	value: any;
	timestamp: number;
	source?: string; // Which action set this value
}

export interface ContextRegistryConfig {
	defaultTTL?: number; // Default time-to-live in milliseconds (default: 30 minutes)
	maxValues?: number; // Maximum number of values to store per key (default: 10)
}

class ContextRegistryClass {
	private static instance: ContextRegistryClass;
	private context = new Map<string, ContextValue[]>();
	private listeners = new Set<(key: string, value: any) => void>();
	private config: ContextRegistryConfig = {
		defaultTTL: 30 * 60 * 1000, // 30 minutes
		maxValues: 10,
	};

	private constructor(config?: ContextRegistryConfig) {
		if (config) {
			this.config = { ...this.config, ...config };
		}
	}

	public static getInstance(
		config?: ContextRegistryConfig,
	): ContextRegistryClass {
		if (!ContextRegistryClass.instance) {
			ContextRegistryClass.instance = new ContextRegistryClass(config);
		}
		return ContextRegistryClass.instance;
	}

	/**
	 * Set a context value
	 */
	set(key: string, value: any, source?: string): void {
		const contextValue: ContextValue = {
			value,
			timestamp: Date.now(),
			source,
		};

		// Get existing values or create new array
		const values = this.context.get(key) || [];

		// Add new value at the beginning
		values.unshift(contextValue);

		// Limit the number of stored values
		if (values.length > this.config.maxValues!) {
			values.length = this.config.maxValues!;
		}

		this.context.set(key, values);

		this.notifyListeners(key, value);
	}

	/**
	 * Get the most recent context value
	 */
	get(key: string, maxAge?: number): any {
		const values = this.context.get(key);
		if (!values || values.length === 0) {
			return undefined;
		}

		const ttl = maxAge || this.config.defaultTTL!;
		const now = Date.now();

		// Find the most recent non-expired value
		for (const contextValue of values) {
			if (now - contextValue.timestamp <= ttl) {
				return contextValue.value;
			}
		}

		// All values expired
		return undefined;
	}

	/**
	 * Get all non-expired context values for a key
	 */
	getAll(key: string, maxAge?: number): any[] {
		const values = this.context.get(key);
		if (!values || values.length === 0) {
			return [];
		}

		const ttl = maxAge || this.config.defaultTTL!;
		const now = Date.now();

		return values
			.filter((cv) => now - cv.timestamp <= ttl)
			.map((cv) => cv.value);
	}

	/**
	 * Get all current context keys and their most recent values
	 */
	getCurrentContext(maxAge?: number): Record<string, any> {
		const context: Record<string, any> = {};

		for (const key of this.context.keys()) {
			const value = this.get(key, maxAge);
			if (value !== undefined) {
				context[key] = value;
			}
		}

		return context;
	}

	/**
	 * Set multiple context values at once
	 */
	setMultiple(values: Record<string, any>, source?: string): void {
		for (const [key, value] of Object.entries(values)) {
			this.set(key, value, source);
		}
	}

	/**
	 * Clear a specific context key
	 */
	clear(key: string): void {
		this.context.delete(key);
	}

	/**
	 * Clear all context
	 */
	clearAll(): void {
		this.context.clear();
	}

	/**
	 * Clean up expired values
	 */
	cleanup(maxAge?: number): void {
		const ttl = maxAge || this.config.defaultTTL!;
		const now = Date.now();

		for (const [key, values] of this.context.entries()) {
			const validValues = values.filter((cv) => now - cv.timestamp <= ttl);

			if (validValues.length === 0) {
				this.context.delete(key);
			} else if (validValues.length < values.length) {
				this.context.set(key, validValues);
			}
		}
	}

	/**
	 * Subscribe to context changes
	 */
	subscribe(listener: (key: string, value: any) => void): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	/**
	 * Notify listeners of context changes
	 */
	private notifyListeners(key: string, value: any): void {
		this.listeners.forEach((listener) => listener(key, value));
	}

	/**
	 * Get debug information
	 */
	getDebugInfo(): any {
		const info: any = {
			totalKeys: this.context.size,
			keys: {},
		};

		for (const [key, values] of this.context.entries()) {
			info.keys[key] = {
				count: values.length,
				mostRecent: values[0]?.value,
				timestamp: values[0]?.timestamp
					? new Date(values[0].timestamp).toISOString()
					: undefined,
				source: values[0]?.source,
			};
		}

		return info;
	}
}

export const ContextRegistry = ContextRegistryClass.getInstance();
